import React, { useEffect, useState } from 'react';
import { fetchSupportAnalyticsAPI } from '../services/serverService';
import { FaChartLine, FaStar, FaUserCheck, FaSpinner, FaExternalLinkAlt } from 'react-icons/fa';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useNavigate } from 'react-router-dom';

export default function SupportStatsPage() {
    const [data, setData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        const loadData = async () => {
            try {
                const res = await fetchSupportAnalyticsAPI();
                setData(res.data.data);
            } catch (err) {
                setError('Failed to load analytics.');
            } finally {
                setIsLoading(false);
            }
        };
        loadData();
    }, []);

    if (isLoading) return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', color: '#3182ce', fontSize: '24px' }}><FaSpinner className="fa-spin" /></div>;
    if (error) return <div style={{ padding: '40px', color: 'red', textAlign: 'center' }}>{error}</div>;

    return (
        <div style={{ padding: '30px', backgroundColor: '#f4f7fa', minHeight: '100vh', fontFamily: 'sans-serif' }}>
            
            <div style={{ marginBottom: '30px' }}>
                <h1 style={{ margin: '0 0 10px 0', color: '#2d3748', fontSize: '28px' }}>Support Analytics</h1>
                <p style={{ margin: 0, color: '#718096' }}>Monitor team performance and customer satisfaction.</p>
            </div>

            {/* Top Stat Cards */}
            <div style={{ display: 'flex', gap: '20px', marginBottom: '30px' }}>
                <div style={{ flex: 1, backgroundColor: 'white', padding: '25px', borderRadius: '10px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)', borderLeft: '4px solid #3182ce' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#718096', marginBottom: '10px', fontWeight: 'bold' }}>
                        <FaChartLine /> Total Tickets Processed
                    </div>
                    <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#2d3748' }}>{data.totalTickets}</div>
                </div>

                {/* HOT ELEMENT: Clickable CSAT Card */}
                <div 
                    onClick={() => navigate('/admin/support/metrics-detail?type=csat')}
                    style={{ flex: 1, backgroundColor: 'white', padding: '25px', borderRadius: '10px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', borderLeft: '4px solid #d69e2e', cursor: 'pointer', transition: 'transform 0.2s', position: 'relative' }}
                    onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                    onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: '#718096', marginBottom: '10px', fontWeight: 'bold' }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}><FaStar /> Global CSAT</span>
                        <FaExternalLinkAlt style={{ fontSize: '12px', color: '#a0aec0' }} />
                    </div>
                    <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#2d3748', display: 'flex', alignItems: 'baseline', gap: '10px' }}>
                        {data.globalCSAT} <span style={{ fontSize: '16px', color: '#a0aec0', fontWeight: 'normal' }}>/ 5.0</span>
                    </div>
                    <div style={{ fontSize: '12px', color: '#3182ce', marginTop: '10px', fontWeight: 'bold' }}>View Rated Tickets →</div>
                </div>
            </div>

            {/* Charts Area */}
            <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '10px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)', marginBottom: '30px' }}>
                <h3 style={{ margin: '0 0 20px 0', color: '#4a5568' }}>Ticket Resolution Trends (Last 30 Days)</h3>
                <div style={{ width: '100%', height: '350px' }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={data.trends}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#edf2f7" />
                            <XAxis dataKey="date" tick={{ fontSize: 12, fill: '#a0aec0' }} axisLine={false} tickLine={false} />
                            <YAxis tick={{ fontSize: 12, fill: '#a0aec0' }} axisLine={false} tickLine={false} />
                            <Tooltip contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }} />
                            <Legend wrapperStyle={{ paddingTop: '10px' }} />
                            <Line type="monotone" dataKey="created" name="New Tickets" stroke="#e53e3e" strokeWidth={3} dot={false} activeDot={{ r: 6 }} />
                            <Line type="monotone" dataKey="resolved" name="Resolved" stroke="#38a169" strokeWidth={3} dot={false} activeDot={{ r: 6 }} />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            </div>

            {/* Agent Performance Table */}
            <div style={{ backgroundColor: 'white', borderRadius: '10px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
                <div style={{ padding: '20px', borderBottom: '1px solid #edf2f7', backgroundColor: '#f7fafc', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <FaUserCheck style={{ color: '#4a5568' }} />
                    <h3 style={{ margin: 0, color: '#4a5568', fontSize: '16px' }}>Agent Performance & QA</h3>
                </div>
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '14px' }}>
                        <thead>
                            <tr style={{ backgroundColor: '#fff', borderBottom: '2px solid #edf2f7', color: '#718096' }}>
                                <th style={{ padding: '15px' }}>Agent Name</th>
                                <th style={{ padding: '15px' }}>Total Handled</th>
                                <th style={{ padding: '15px' }}>Currently Open</th>
                                <th style={{ padding: '15px' }}>QA Follow-ups</th>
                                <th style={{ padding: '15px' }}>Avg CSAT</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.agentStats.map((agent, index) => (
                                <tr 
                                    key={index} 
                                    onClick={() => navigate(`/support?assignee=${agent.uid}`)}
                                    style={{ borderBottom: '1px solid #edf2f7', cursor: 'pointer', transition: 'background-color 0.2s' }}
                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#ebf8ff'}
                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                >
                                    <td style={{ padding: '15px', fontWeight: 'bold', color: '#2b6cb0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        {agent.name} <FaExternalLinkAlt style={{ fontSize: '10px', color: '#a0aec0' }} />
                                    </td>
                                    <td style={{ padding: '15px' }}>{agent.totalHandled}</td>
                                    <td style={{ padding: '15px', color: '#3182ce' }}>{agent.open}</td>
                                    <td style={{ padding: '15px' }}>
                                        {agent.followUpsPending > 0 ? (
                                            <span style={{ backgroundColor: '#fed7d7', color: '#c53030', padding: '4px 8px', borderRadius: '12px', fontWeight: 'bold', fontSize: '12px' }}>{agent.followUpsPending} Pending</span>
                                        ) : (
                                            <span style={{ color: '#a0aec0', fontSize: '12px' }}>0</span>
                                        )}
                                    </td>
                                    <td style={{ padding: '15px' }}>
                                        {agent.avgCSAT > 0 ? (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '5px', color: agent.avgCSAT >= 4 ? '#d69e2e' : '#e53e3e', fontWeight: 'bold' }}>
                                                {agent.avgCSAT} <FaStar />
                                            </div>
                                        ) : <span style={{ color: '#a0aec0', fontSize: '12px' }}>No Data</span>}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}